<template>
  <v-container fluid>
    <div class="d-flex flex-column flex-md-row justify-space-between">
      <div>
        <h1>{{ $t('EDIT_ACCOUNT_PAGE.TITLE.HEADER') }}</h1>
        <span>{{ $t('EDIT_ACCOUNT_PAGE.TITLE.SUBTITLE') }}</span>
      </div>
    </div>

    <v-form ref="formEditAccount" v-model="formEditAccountValid" @submit.prevent="submit">
      <v-row>
        <v-col cols="12" md="6" class="d-flex">
          <v-card class="rounded-xl mt-8 flex-fill" :border="false" :elevation="0">
            <v-row>
              <v-col cols="12">
                <h3 class="font-weight-bold">{{ $t('EDIT_ACCOUNT_PAGE.FORM.ACCOUNT_INFO') }}</h3>
              </v-col>
              <v-col cols="12" md="6">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_ACCOUNT_TYPE') }}</p>
                <v-select
                  v-model="has_several_collaborators"
                  :disabled="!can_update_account_type"
                  :items="types"
                  :rules="[...requiredRules, ...isBooleanRules]"
                  required
                >
                  <template v-slot:selection="{ item }">
                    <v-chip size="small">
                      <span>{{ item.title }}</span>
                    </v-chip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_ID') }}</p>
                <v-text-field
                  v-model="technical_id"
                  :error-messages="error?.technical_id"
                  :rules="requiredRules"
                  required
                  v-on:input="error = null"
                  disabled
                />
              </v-col>
              <v-col cols="12" md="6">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_PHONE') }}</p>
                <v-phone-input
                  v-model="phone"
                  v-model:country="selectedCountry"
                  :includeCountries="allCountries"
                  :rules="requiredRules"
                  :error-messages="error?.phone"
                />
              </v-col>
              <v-col cols="12" md="6">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_EMAIL') }}</p>
                <v-text-field v-model="email" disabled required />
              </v-col>
              <v-col cols="12" md="6">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_APPLICANT_EMAIL') }}</p>
                <v-text-field v-model="applicant_email" required />
              </v-col>
              <v-col cols="12" md="6">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_STATUS') }}</p>
                <div class="d-flex align-center">
                  <label>{{ getStatus() }}</label>
                  <v-switch
                    v-model="status"
                    :indeterminate="status === 'pending'"
                    :rules="requiredRules"
                    class="v-switch d-flex justify-end"
                    color="#559a1b"
                    false-value="inactive"
                    inset
                    true-value="active"
                  />
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <v-card class="rounded-xl mt-8 flex-fill" :border="false" :elevation="0">
            <v-row>
              <v-col cols="12">
                <h3 class="font-weight-bold">{{ $t('EDIT_ACCOUNT_PAGE.FORM.USER_SCOPE') }}</h3>
              </v-col>
              <v-col cols="12">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_ROLE') }}</p>
                <v-select
                  id="select-role"
                  v-model="selectedRoles"
                  :items="roles"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="[...requiredRules, ...requiredMultipleRules]"
                  item-title="title"
                  item-value="technical_id"
                  multiple
                  required
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip size="small" v-if="index < 1">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="text-grey text-caption align-self-center">
                      (+{{ selectedRoles.length - 1 }}
                      {{
                        selectedRoles.length - 1 === 1
                          ? $t('EDIT_ACCOUNT_PAGE.FORM.OTHER')
                          : $t('EDIT_ACCOUNT_PAGE.FORM.OTHERS')
                      }})
                    </span>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item
                      :disabled="isDisabled(item)"
                      :title="item.title"
                      :value="item.value"
                      v-bind="props"
                    >
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-role')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_NETWORK') }}</p>
                <v-select
                  disabled
                  id="select-network"
                  v-model="selectedNetwork"
                  :items="networks"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="requiredRules"
                  item-title="name"
                  item-value="technical_id"
                  required
                >
                  <template v-slot:selection="{ item }">
                    <v-chip size="small">
                      <span>{{ item.title }}</span>
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item :title="item.title" :value="item.value" v-bind="props">
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-network')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <p data-custom="true">{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_GROUPS') }}</p>
                <v-select
                  id="select-group"
                  v-model="selectedGroups"
                  :disabled="!selectedNetwork"
                  :items="groups"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="selectedStores.length ? [] : [...requiredRules, ...requiredMultipleRules]"
                  item-title="name"
                  item-value="technical_id"
                  multiple
                  required
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip size="small" v-if="index < 1">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="text-grey text-caption align-self-center">
                      (+{{ selectedGroups.length - 1 }}
                      {{
                        selectedGroups.length - 1 === 1
                          ? $t('EDIT_ACCOUNT_PAGE.FORM.OTHER')
                          : $t('EDIT_ACCOUNT_PAGE.FORM.OTHERS')
                      }})
                    </span>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item :title="item.title" :value="item.value" v-bind="props">
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-group')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_STORES') }}</p>
                <v-select
                  id="select-store"
                  v-model="selectedStores"
                  :disabled="!selectedNetwork"
                  :items="stores"
                  :no-data-text="$t('COMMON.NO_DATA')"
                  :rules="selectedGroups.length ? [] : [...requiredRules, ...requiredMultipleRules]"
                  base-color="#559a1b"
                  item-title="name"
                  item-value="technical_id"
                  multiple
                  required
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip size="small" v-if="index < 1">
                      <span>{{ item.title }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="text-grey text-caption align-self-center">
                      (+{{ selectedStores.length - 1 }}
                      {{
                        selectedStores.length - 1 === 1
                          ? $t('EDIT_ACCOUNT_PAGE.FORM.OTHER')
                          : $t('EDIT_ACCOUNT_PAGE.FORM.OTHERS')
                      }})
                    </span>
                  </template>
                  <template v-slot:item="{ item, props }">
                    <v-list-item :title="item.title" :value="item.value" v-bind="props">
                      <v-tooltip
                        v-if="shouldShowTooltip(item, 'select-store')"
                        activator="parent"
                        location="bottom left"
                      >
                        {{ item.title }}
                      </v-tooltip>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col class="d-flex">
          <v-card class="rounded-xl mt-8 flex-fill" :border="false" :elevation="0">
            <v-row>
              <v-col cols="12">
                <h3 class="font-weight-bold">{{ $t('EDIT_ACCOUNT_PAGE.FORM.ADDITIONAL_INFO') }}</h3>
              </v-col>
              <v-col cols="4" md="4">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_FORMATION_ONE') }}</p>
                <v-textarea
                  :counter="255"
                  :maxlength="255"
                  v-model.trim="formation_one"
                  auto-grow
                  rows="2"
                />
              </v-col>
              <v-col cols="4" md="4">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_FORMATION_TWO') }}</p>
                <v-textarea
                  :counter="255"
                  :maxlength="255"
                  v-model.trim="formation_two"
                  auto-grow
                  rows="2"
                />
              </v-col>
              <v-col cols="4" md="4">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_FORMATION_THREE') }}</p>
                <v-textarea
                  :counter="255"
                  :maxlength="255"
                  v-model.trim="formation_three"
                  auto-grow
                  rows="2"
                />
              </v-col>
              <v-col cols="4" md="4">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_DESCRIPTION') }}</p>
                <v-textarea
                  :counter="255"
                  :maxlength="255"
                  v-model.trim="description"
                  auto-grow
                  rows="2"
                  v-on:input="error = null"
                  :error-messages="error?.description"
                />
              </v-col>
              <v-col cols="4" md="4">
                <p>{{ $t('EDIT_ACCOUNT_PAGE.FORM.LABEL_SUB_STATUS') }}</p>
                <v-textarea
                  :counter="255"
                  :maxlength="255"
                  v-model.trim="sub_status"
                  auto-grow
                  rows="2"
                  v-on:input="error = null"
                  :error-messages="error?.sub_status"
                />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" class="d-flex justify-start">
          <v-btn
            v-if="checkPermission(['DISTRIBUTOR_ACCOUNT_UPDATE_PERMISSION'])"
            :disabled="!formEditAccountValid || loading"
            :loading="loading"
            class="boom-button mr-4"
            type="submit"
            variant="outlined"
          >
            {{ $t('EDIT_ACCOUNT_PAGE.BUTTON.VALIDATE') }}
          </v-btn>

          <v-btn
            :to="'/accounts/detail/' + `${$route.params.accountId}`"
            class="boom-button-back"
            variant="outlined"
          >
            {{ $t('COMMON.BUTTON.BACK') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import RoleService from '@/services/RoleService'
import AccountService from '@/services/AccountService'
import RegionCodeService from '@/services/RegionCodeService'
import ResponseConstant from '@/constants/responseConstant'
import { VPhoneInput } from 'v-phone-input'
import router from '@/router'
import Validator from '@/components/utils/validator'
import GroupService from '@/services/GroupService'
import StoreService from '@/services/StoreService'
import { displayToast, handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'

export default {
  name: 'EditAccountPage',
  components: { VPhoneInput },
  data() {
    return {
      loading: false,
      formEditAccountValid: false,
      account: null,
      has_several_collaborators: null,
      can_update_account_type: true,
      types: [
        { value: false, title: this.$t('EDIT_ACCOUNT_PAGE.FORM.SOLO') },
        { value: true, title: this.$t('EDIT_ACCOUNT_PAGE.FORM.MULTIPLE') }
      ],
      selectedCountry: '',
      technical_id: null,
      phone: null,
      email: null,
      applicant_email: null,
      status: null,
      formation_one: null,
      formation_two: null,
      formation_three: null,
      description: null,
      sub_status: null,
      allCountries: [],
      roles: [],
      selectedRoles: [],
      networks: [],
      selectedNetwork: null,
      groups: [],
      selectedGroups: [],
      stores: [],
      selectedStores: [],
      requiredRules: Validator.requiredRules,
      requiredMultipleRules: Validator.requiredMultipleRules,
      isBooleanRules: Validator.isBooleanRules,

      error: null
    }
  },
  watch: {
    selectedNetwork() {
      this.onNetworkChange()
    }
  },
  async created() {
    try {
      const response = await RoleService.getRoleList()
      this.roles = response.item_list

      this.account = await AccountService.getAccount(this.$route.params.accountId)
      this.networks = [this.account.network]
      this.patchAccount()
    } catch (error) {
      handleResponseError(error)
    }
    await this.getRegionCode()
  },
  methods: {
    shouldShowTooltip,
    patchAccount() {
      const self = this
      this.has_several_collaborators = this.account.has_several_collaborators
      if (this.has_several_collaborators) {
        this.can_update_account_type = false
      }
      this.technical_id = this.account.technical_id
      this.phone = this.account.phone_number
      this.email = this.account.email
      this.applicant_email = this.account.applicant_email
      this.status = this.account.status
      this.formation_one = this.account.formation_one
      this.formation_two = this.account.formation_two
      this.formation_three = this.account.formation_three
      this.description = this.account.description
      this.sub_status = this.account.sub_status
      this.selectedRoles = this.account.roles
        .filter(function (obj) {
          return self.roles.indexOf(obj) === -1
        })
        .map(function (role) {
          return role.technical_id
        })
      const foundNetwork = this.networks.find((network) => {
        return network.technical_id === parseInt(self.account.network_id)
      })
      if (foundNetwork) {
        this.selectedNetwork = foundNetwork.technical_id
      }
    },
    async onNetworkChange() {
      this.groups = []
      this.selectedGroups = []
      this.stores = []
      this.selectedStores = []

      try {
        const responseGroup = await GroupService.getAllAccountGroup(this.selectedNetwork)
        this.groups = responseGroup.item_list

        const responseStore = await StoreService.getAllAccountStore(this.selectedNetwork)
        this.stores = responseStore.item_list
        this.patchSelectedNetwork()
      } catch (error) {
        handleResponseError(error)
      }
    },
    async getRegionCode() {
      this.loading = true
      try {
        const response = await RegionCodeService.getRegionCodeList()
        this.allCountries = this.getCountryCode(response.item_list)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    patchSelectedNetwork() {
      if (this.selectedNetwork === parseInt(this.account.network_id)) {
        const self = this
        this.selectedGroups = this.account.group_list
          .filter(function (obj) {
            return self.groups.indexOf(obj) === -1
          })
          .map(function (entity) {
            return entity.technical_id
          })

        this.selectedStores = this.account.store_list
          .filter(function (obj) {
            return self.stores.indexOf(obj) === -1
          })
          .map(function (entity) {
            return entity.technical_id
          })
      }
    },
    isDisabled(item) {
      const roleFiltered = this.roles.filter((role) =>
        this.selectedRoles.includes(role.technical_id)
      )
      if (roleFiltered.length) {
        if (item.raw.type !== roleFiltered[0].type) {
          return true
        }
      }
    },
    getCountryCode(zones) {
      return zones.map((item) => {
        return item.country_code
      })
    },
    getStatus() {
      switch (this.status) {
        case 'active':
          return this.$t('STATUS.ACTIVE')
        case 'inactive':
          return this.$t('STATUS.INACTIVE')
        default:
          return this.$t('STATUS.PENDING')
      }
    },
    checkPermission(permissionsRequired) {
      return this.$store.getters['auth/hasPermission'](permissionsRequired)
    },
    async submit() {
      this.loading = true
      const data = {
        account_id: this.$route.params.accountId,
        technical_id: this.technical_id,
        status: this.status,
        phone_number: this.phone,
        network_id: this.selectedNetwork,
        role_id_list: this.selectedRoles,
        group_id_list: this.selectedGroups,
        store_id_list: this.selectedStores,
        has_several_collaborators: this.has_several_collaborators,
        applicant_email: this.applicant_email,
        formation_one: this.formation_one,
        formation_two: this.formation_two,
        formation_three: this.formation_three,
        description: this.description,
        sub_status: this.sub_status
      }
      try {
        await AccountService.updateAccount(data)
        displayToast(this.$t('EDIT_ACCOUNT_PAGE.MESSAGE.SUCCESS'), 'success')
        await router.push({ path: '/accounts/detail/' + this.$route.params.accountId })
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      } finally {
        this.loading = false
      }
    },
    setError(key) {
      switch (key) {
        case 'invalid_phone_number_error_key':
          this.error = { phone: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    }
  }
}
</script>

<style scoped>
.v-container {
  padding: 32px;
}

.v-card {
  padding: 32px;
}

.v-form p {
  margin-bottom: 16px;
}

:deep(.v-chip) {
  color: #559a1b;
}
</style>
